import { ChevronDownIcon, XMarkIcon } from '@heroicons/react/16/solid';
import { GoInfo } from 'react-icons/go';
import { formatPrevalenceClass, getPrevalenceVisualData, parsePrevalenceData } from '@/utils/prevalence';

type PrevalenceBreakdownPanelProps = {
  rowData: any;
  onClose: () => void;
};

export function PrevalenceBreakdownPanel({ rowData, onClose }: PrevalenceBreakdownPanelProps) {
  const diseaseLabel = rowData?.diseaseName || rowData?.name || rowData?.diseaseUMLSCUI || '';
  const entries = parsePrevalenceData({
    disease_prevalence_class: rowData?.disease_prevalence_class,
    disease_prevalence_geo_area: rowData?.disease_prevalence_geo_area,
    disease_prevalence_type: rowData?.disease_prevalence_type,
  });

  const groupedByType = entries.reduce<Record<string, typeof entries>>((acc, entry) => {
    const key = entry.type || 'Unknown type';
    if (!acc[key]) acc[key] = [];
    acc[key].push(entry);
    return acc;
  }, {});

  return (
    <div className="flex flex-col h-full bg-white overflow-hidden">
      <div className="flex items-center justify-between flex-shrink-0 px-4 py-3 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-1.5 min-w-0">
          <GoInfo className="w-5 h-4 stroke-1 text-blue m-1" />
          <h2 className="text-sm font-semibold text-gray-900 truncate min-w-0 mb-0 pb-0">
            Prevalence Breakdown
          </h2>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-200 flex-shrink-0"
          aria-label="Close prevalence breakdown"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      </div>

      {diseaseLabel && (
        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100">
          <p className="text-xs font-medium text-blue-800 truncate mb-0" title={diseaseLabel}>
            {diseaseLabel}
          </p>
        </div>
      )}

      <div className="flex-1 overflow-y-auto p-3 min-h-0">
        {entries.length === 0 ? (
          <p className="text-sm text-gray-400 text-center mt-6">No prevalence data available.</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedByType).map(([type, typeEntries]) => (
              <section key={type}>
                <details className="group">
                  <summary className="cursor-pointer list-none flex items-center justify-between mb-2 focus:outline-none focus-visible:outline-none">
                    <h3 className="text-sm font-semibold text-pink mb-0">{type}</h3>
                    <ChevronDownIcon className="w-4 h-4 text-gray-500 group-open:rotate-180 transition-transform" />
                  </summary>
                  <ul className="space-y-1 mb-0">
                    {typeEntries.map((entry, index) => (
                      <li key={`${type}-${entry.geography}-${index}`} className="text-sm text-gray-700 mb-4">
                        <div className="font-medium">
                          {entry.geography || 'Unknown region'} -{' '}
                          <span className="text-gray-500 text-xs">
                            {formatPrevalenceClass(entry.class || 'Unknown')}
                          </span>
                        </div>
                        {(() => {
                          const visual = getPrevalenceVisualData(entry.class);
                          if (!visual) return null;

                          return (
                            <div className="mt-1">
                              <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                                <div
                                  className="bg-blue rounded-full h-1.5"
                                  style={{ width: `${visual.barPercent}%` }}
                                />
                              </div>
                            </div>
                          );
                        })()}
                      </li>
                    ))}
                  </ul>
                </details>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
